/**
 * ReadingPanel — rich renderer for content_panel body_markdown.
 *
 * Detects orchestration-pattern ## headings and renders them as a card
 * grid with Use/Avoid/Watch-out callouts. Tables and Quick chooser
 * sections get dedicated treatments. Falls back to readable prose.
 */
import React, { useMemo, useRef } from 'react';
import './ReadingPanel.css';

const PATTERN_KEYS = {
  pipeline: 'pipeline',
  router: 'router',
  swarm: 'swarm',
  hierarchical: 'hierarchical',
  'manager + workers': 'hierarchical',
  'manager+workers': 'hierarchical',
};

function renderInline(text) {
  if (!text) return null;
  const parts = String(text).split(/(\*\*[^*]+\*\*|`[^`]+`)/g);
  return parts.map((p, i) => {
    if (p.startsWith('**') && p.endsWith('**')) {
      return <strong key={i}>{p.slice(2, -2)}</strong>;
    }
    if (p.startsWith('`') && p.endsWith('`')) {
      return (
        <code
          key={i}
          style={{
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
            fontSize: '0.9em',
            background: 'rgba(15, 118, 110, 0.1)',
            padding: '0.05em 0.3em',
            borderRadius: 4,
          }}
        >
          {p.slice(1, -1)}
        </code>
      );
    }
    return <React.Fragment key={i}>{p}</React.Fragment>;
  });
}

function matchPatternKey(title) {
  const t = (title || '').trim().toLowerCase();
  for (const [needle, key] of Object.entries(PATTERN_KEYS)) {
    if (t === needle || t.startsWith(`${needle} `) || t.includes(`(${needle})`)) {
      return key;
    }
    if (t.startsWith(needle)) return key;
  }
  // "1. Pipeline (Sequential)" style
  const m = t.match(/^\d+\.\s*(pipeline|router|swarm|hierarchical)/);
  if (m) return m[1];
  return null;
}

function parseCalloutLine(line) {
  const raw = line.replace(/^[-*]\s+/, '').trim();
  const m = raw.match(/^\*\*(Use when|Avoid when|Watch-out|Watch out|Think of it as|Rule of thumb):\*\*\s*(.*)$/i);
  if (!m) return null;
  const label = m[1].toLowerCase();
  let kind = 'watch';
  if (label.startsWith('use')) kind = 'use';
  else if (label.startsWith('avoid')) kind = 'avoid';
  else if (label.startsWith('think') || label.startsWith('rule')) kind = 'use';
  return { kind, label: m[1], text: m[2] };
}

function isTableSep(line) {
  return /^\|?\s*:?-{3,}/.test(line.trim());
}

function parseTable(lines, start) {
  const rows = [];
  let i = start;
  while (i < lines.length && lines[i].includes('|')) {
    const cells = lines[i]
      .trim()
      .replace(/^\|/, '')
      .replace(/\|$/, '')
      .split('|')
      .map((c) => c.trim());
    if (!isTableSep(lines[i])) rows.push(cells);
    i += 1;
  }
  return { rows, next: i };
}

function parseSections(text) {
  const lines = String(text || '').replace(/\r\n/g, '\n').split('\n');
  let title = '';
  let lede = [];
  const sections = [];
  let current = null;
  let i = 0;

  const flush = () => {
    if (current) sections.push(current);
    current = null;
  };

  while (i < lines.length) {
    const line = lines[i];
    if (line.startsWith('# ') && !title) {
      title = line.slice(2).trim();
      i += 1;
      continue;
    }
    if (line.startsWith('## ')) {
      flush();
      const heading = line.slice(3).trim();
      current = {
        heading,
        patternKey: matchPatternKey(heading),
        blocks: [],
      };
      i += 1;
      continue;
    }
    if (!current) {
      if (line.trim()) lede.push(line);
      i += 1;
      continue;
    }

    if (line.includes('|') && i + 1 < lines.length && isTableSep(lines[i + 1])) {
      const { rows, next } = parseTable(lines, i);
      if (rows.length) current.blocks.push({ type: 'table', rows });
      i = next;
      continue;
    }

    const callout = parseCalloutLine(line);
    if (callout) {
      current.blocks.push({ type: 'callout', ...callout });
      i += 1;
      continue;
    }

    if (line.startsWith('> ')) {
      current.blocks.push({ type: 'quote', text: line.slice(2) });
      i += 1;
      continue;
    }

    if (line.startsWith('- ') || line.startsWith('* ')) {
      const items = [];
      while (i < lines.length && (lines[i].startsWith('- ') || lines[i].startsWith('* '))) {
        const c = parseCalloutLine(lines[i]);
        if (c) {
          if (items.length) {
            current.blocks.push({ type: 'list', items: [...items] });
            items.length = 0;
          }
          current.blocks.push({ type: 'callout', ...c });
        } else {
          items.push(lines[i].slice(2));
        }
        i += 1;
      }
      if (items.length) current.blocks.push({ type: 'list', items });
      continue;
    }

    if (/^\d+\.\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\d+\.\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\d+\.\s+/, ''));
        i += 1;
      }
      current.blocks.push({ type: 'olist', items });
      continue;
    }

    if (!line.trim()) {
      i += 1;
      continue;
    }

    // paragraph: gather consecutive non-empty non-special lines
    const para = [];
    while (
      i < lines.length
      && lines[i].trim()
      && !lines[i].startsWith('#')
      && !lines[i].startsWith('> ')
      && !lines[i].startsWith('- ')
      && !lines[i].startsWith('* ')
      && !/^\d+\.\s+/.test(lines[i])
      && !(lines[i].includes('|') && i + 1 < lines.length && isTableSep(lines[i + 1]))
    ) {
      para.push(lines[i].trim());
      i += 1;
    }
    if (para.length) current.blocks.push({ type: 'p', text: para.join(' ') });
  }
  flush();

  return {
    title,
    lede: lede.join('\n').trim(),
    sections,
  };
}

function PatternCard({ section, delay }) {
  const body = section.blocks.filter((b) => b.type === 'p').map((b) => b.text).join(' ');
  const callouts = section.blocks.filter((b) => b.type === 'callout');
  const lists = section.blocks.filter((b) => b.type === 'list' || b.type === 'olist');

  return (
    <article
      className={`reading-panel__pattern reading-panel__pattern--${section.patternKey}`}
      style={{ animationDelay: `${delay}ms` }}
      id={`rp-${section.patternKey}`}
    >
      <div className="reading-panel__pattern-kicker">Pattern</div>
      <h3 className="reading-panel__pattern-title">{section.heading}</h3>
      {body ? <p className="reading-panel__pattern-body">{renderInline(body)}</p> : null}
      {lists.map((block, i) => (
        <ul key={`l-${i}`} className="reading-panel__list">
          {block.items.map((item, j) => (
            <li key={j}>{renderInline(item)}</li>
          ))}
        </ul>
      ))}
      {callouts.map((c, i) => (
        <div key={i} className={`reading-panel__callout reading-panel__callout--${c.kind}`}>
          <strong>{c.label}:</strong> {renderInline(c.text)}
        </div>
      ))}
    </article>
  );
}

function ProseSection({ section }) {
  const isChooser = /quick\s*(chooser|decision)|before you continue|choosing a pattern/i.test(
    section.heading || ''
  );

  if (isChooser) {
    return (
      <div className="reading-panel__chooser" id="rp-chooser">
        <h3>{section.heading}</h3>
        {section.blocks.map((b, i) => {
          if (b.type === 'olist' || b.type === 'list') {
            const Tag = b.type === 'olist' ? 'ol' : 'ul';
            return (
              <Tag key={i}>
                {b.items.map((item, j) => (
                  <li key={j}>{renderInline(item)}</li>
                ))}
              </Tag>
            );
          }
          if (b.type === 'table') {
            return (
              <div key={i} className="reading-panel__table-wrap" style={{ marginTop: 10, background: 'rgba(255,255,255,0.1)', borderColor: 'rgba(255,255,255,0.2)' }}>
                <table className="reading-panel__table">
                  <thead>
                    <tr>
                      {b.rows[0]?.map((c, ci) => (
                        <th key={ci} style={{ background: 'rgba(255,255,255,0.12)', color: '#fff' }}>{renderInline(c)}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {b.rows.slice(1).map((row, ri) => (
                      <tr key={ri}>
                        {row.map((c, ci) => (
                          <td key={ci} style={{ color: '#ecfdf5', borderColor: 'rgba(255,255,255,0.15)' }}>{renderInline(c)}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          }
          if (b.type === 'p') return <p key={i} style={{ margin: '0.35rem 0', opacity: 0.95 }}>{renderInline(b.text)}</p>;
          return null;
        })}
      </div>
    );
  }

  return (
    <section>
      {section.heading ? (
        <h3 className="reading-panel__section-title" id={`rp-sec-${section.heading.slice(0, 24).replace(/\s+/g, '-')}`}>
          {section.heading}
        </h3>
      ) : null}
      {section.blocks.map((b, i) => {
        if (b.type === 'p') return <p key={i} className="reading-panel__p">{renderInline(b.text)}</p>;
        if (b.type === 'quote') {
          return <blockquote key={i} className="reading-panel__quote">{renderInline(b.text)}</blockquote>;
        }
        if (b.type === 'callout') {
          return (
            <div key={i} className={`reading-panel__callout reading-panel__callout--${b.kind}`}>
              <strong>{b.label}:</strong> {renderInline(b.text)}
            </div>
          );
        }
        if (b.type === 'list' || b.type === 'olist') {
          const Tag = b.type === 'olist' ? 'ol' : 'ul';
          return (
            <Tag key={i} className="reading-panel__list">
              {b.items.map((item, j) => (
                <li key={j}>{renderInline(item)}</li>
              ))}
            </Tag>
          );
        }
        if (b.type === 'table') {
          return (
            <div key={i} className="reading-panel__table-wrap">
              <table className="reading-panel__table">
                <thead>
                  <tr>
                    {b.rows[0]?.map((c, ci) => (
                      <th key={ci}>{renderInline(c)}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {b.rows.slice(1).map((row, ri) => (
                    <tr key={ri}>
                      {row.map((c, ci) => (
                        <td key={ci}>{renderInline(c)}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        }
        return null;
      })}
    </section>
  );
}

export default function ReadingPanel({ text, eyebrow = 'Reading' }) {
  const rootRef = useRef(null);
  const parsed = useMemo(() => parseSections(text), [text]);

  if (!text) return null;

  const patternSections = parsed.sections.filter((s) => s.patternKey);
  const otherSections = parsed.sections.filter((s) => !s.patternKey);

  const scrollTo = (id) => {
    const el = rootRef.current?.querySelector(`#${id}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="reading-panel" ref={rootRef}>
      <header className="reading-panel__hero">
        <p className="reading-panel__eyebrow">{eyebrow}</p>
        {parsed.title ? <h2 className="reading-panel__title">{parsed.title}</h2> : null}
        {parsed.lede ? (
          <p className="reading-panel__lede">
            {parsed.lede.split('\n').map((line, i) => (
              <React.Fragment key={i}>
                {i > 0 ? <br /> : null}
                {renderInline(line)}
              </React.Fragment>
            ))}
          </p>
        ) : null}
      </header>

      {patternSections.length >= 2 ? (
        <nav className="reading-panel__toc" aria-label="Jump to patterns">
          {patternSections.map((s) => (
            <button
              key={s.patternKey}
              type="button"
              className="reading-panel__toc-btn"
              onClick={() => scrollTo(`rp-${s.patternKey}`)}
            >
              {s.heading.replace(/^\d+\.\s*/, '').split(/[—(]/)[0].trim()}
            </button>
          ))}
          {otherSections.some((s) => /quick|chooser|decision|choosing/i.test(s.heading)) ? (
            <button type="button" className="reading-panel__toc-btn" onClick={() => scrollTo('rp-chooser')}>
              Quick chooser
            </button>
          ) : null}
        </nav>
      ) : null}

      <div className="reading-panel__body">
        {patternSections.length ? (
          <div className="reading-panel__pattern-grid">
            {patternSections.map((s, idx) => (
              <PatternCard key={s.patternKey || s.heading} section={s} delay={idx * 70} />
            ))}
          </div>
        ) : null}
        {otherSections.map((s) => (
          <ProseSection key={s.heading} section={s} />
        ))}
      </div>
    </div>
  );
}
